import {Collapse, Grid,} from "@mui/material";
import {memo, useEffect, useState, useCallback} from "react";
import MhdSelect from "../atomicComponents/MhdSelect";
import MhdInput from "../atomicComponents/MhdInput";
import {dataProductChoices, solutionChoices, timeSeriesVariableChoices, variableChoices,} from "../../utils/choices";
import MhdDatetime from "../atomicComponents/MhdDatetime";
import {getStates} from "../../utils/api";
import dayjs from "dayjs";
import StyledDivider from "../atomicComponents/StyledDivider";
import {noDataProductUsage, noDomainUsage, noVariableUsage} from "../../utils/globals";
import MhdTimeSlider from "../atomicComponents/MhdTimeSlider";
import {setAndUpdate} from "../../utils/helpers";
import {useDatetimeCarringtonConverter} from "../../hooks/useDatetimeCarringtonConverter";

const SimWidget = ({data, mods, warn, errs, update, setter, tool}) => {
  const [cri, setCri] = useState(null)
  const [stateId, setStateId] = useState(null)
  const [states, setStates] = useState([])
  const { dtToCR, crToDT } = useDatetimeCarringtonConverter(setter, update);

  useEffect(() => {
    dtToCR('time_of_interest', data.time_of_interest ? dayjs(data.time_of_interest).format("YYYY-MM-DD HH:mm:ss") : '', setCri, false)
  }, [data.cor_id]);

  useEffect(() => {
    const params = {
      cor_id: data.cor_id,
      domain: mods.domain ?? data.domain,
    }
    getStates(params)
      .then(response => {
        setStates(response.data)
      })

  }, [data.cor_id, data.domain,  data.data_product, mods.domain, mods.data_product]);

  useEffect(() => {
    if (states.length !== 0) {
      const targetDate = mods.time_of_interest ? dayjs(mods.time_of_interest) : data.time_of_interest ? dayjs(data.time_of_interest) : null;
      const closestState = states.reduce((closest, state) => {
        const diff = Math.abs(dayjs(state.time).diff(targetDate));
        return diff < closest.diff ? { id: state.id, diff } : closest;
      }, { id: null, diff: Infinity });

      setStateId(closestState.id);
    }
  }, [data.time_of_interest, mods.time_of_interest, data.cor_id, states]);

  const extractStates = () => {
    if (states) {
      return states.map(row => (
        {
          value: row.id,
          label: `Instantaneous Time: ${dayjs(row.time).format("YYYY-MM-DD HH:mm:ss")}`,
          secondaryLabel: ` [${dayjs(row.state_t0).format("YYYY-MM-DD HH:mm:ss")} – ${dayjs(row.state_t1).format("YYYY-MM-DD HH:mm:ss")}]`,
        })
      );
    }
    return [{ value: '', label: ''}];
  };

  const handleFieldChange = (k, v) => {
    setAndUpdate(k, v, setter, update)
  }

  const handleStateChange = (v) => {
    const row = states.find(item => item.id === v);
    setStateId(v)
    dtToCR('time_of_interest', row.time, setCri)
  }


  return (
    <Grid container spacing={1} pt={1}>
      <Grid item xs={12}>
        <MhdTimeSlider
          label="States"
          ti={!['', null, undefined].includes(data.time_of_interest) ? data.time_of_interest : mods.time_of_interest}
          onChange={useCallback((v) => dtToCR('time_of_interest', v, setCri), [])}
          states={states}
          selectedState={stateId}
        />
      </Grid>
      <Grid item xs={12}>
        <MhdSelect
          label="State of Interest"
          value={stateId}
          onChange={handleStateChange}
          choices={extractStates(states)}
        />
      </Grid>
      <Grid item xs={7}>
        <MhdDatetime
          label={"Time of Interest"}
          value={data.time_of_interest}
          override={mods.time_of_interest}
          warning={warn.time_of_interest}
          error={errs.time_of_interest}
          onChange={useCallback((v) => dtToCR('time_of_interest', v, setCri), [])}
        />
      </Grid>
      <Grid item xs={5}>
        <MhdInput
          label={"CR of Interest"}
          value={cri}
          onChange={useCallback((v) => crToDT('time_of_interest', v, setCri), [])}
          units={"CR"}
          clear={true}
        />
      </Grid>

      {!(noDomainUsage.includes(tool) && noVariableUsage.includes(tool) && noDataProductUsage.includes(tool)) && (
        <Grid item xs={12} mt={1}>
          <StyledDivider />
        </Grid>
      )}
      {!noDomainUsage.includes(tool) && (
        <Grid item xs={4}>
          <MhdSelect
            label="Domain"
            value={data.domain}
            override={mods.domain}
            warning={warn.domain}
            error={errs.domain}
            onChange={useCallback((v) => handleFieldChange('domain', v), [])}
            choices={solutionChoices}
          />
        </Grid>
      )}

      {/* Conditionally render the Variable widget */}
      {!noVariableUsage.includes(tool) && (
        <Grid item xs={4}>
          <Collapse in={(noDataProductUsage.includes(tool) || data.data_product !== 'chmap')}>
            <MhdSelect
              label="Variable"
              value={data.variable}
              override={mods.variable}
              warning={warn.variable}
              error={errs.variable}
              onChange={useCallback((v) => handleFieldChange('variable', v), [])}
              choices={tool !== 'time_series' ? variableChoices : timeSeriesVariableChoices}
            />
          </Collapse>
        </Grid>
      )}

      {/* Conditionally render the Data Product widget */}
      {!noDataProductUsage.includes(tool) && (
        <Grid item xs={4}>
          <MhdSelect
            label="Data Product"
            value={data.data_product ?? 'null'}
            override={mods.data_product}
            warning={warn.data_product}
            error={errs.data_product}
            onChange={useCallback((v) => handleFieldChange('data_product', v), [])}
            choices={dataProductChoices}
          />
        </Grid>
      )}
    </Grid>
  );
}

export default memo(SimWidget);